import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { useQueryManager } from "@/composables/useQueryManager";
import type { StepConfig } from "@/composables/useStepFunctions";
import { useSignupStore } from "@/signup/signup";
import type { GeneratorUploadResult } from "@/types";
import useVuelidate, { type Validation } from "@vuelidate/core";
import { isEqual } from "lodash-es";
import type { Ref } from "vue";
import { useRoute } from "vue-router";

/**
 * How a signup flow's steps save: the shown step is validated, the draft saved, and the ids the
 * server assigned go into the URL, so the next save names the company and generator it created.
 *
 * Call it in the flow's setup. Without `v$` it validates the flow's own child steps: a bare
 * `useVuelidate()` collects the validations of the components under the instance that calls it.
 */
export function useSignupStepSave(
  isLoading: Ref<boolean>,
  v$: Ref<Validation> = useVuelidate()
) {
  const signupStore = useSignupStore();
  const { updateQuery } = useQueryManager();
  const route = useRoute();
  const { globalErrorHandler } = useGlobalHandlers();

  /** A step's `postStepCallback`: false keeps the flow on the step, and a failed save is reported. */
  async function postStepCallback({
    apiEndpoint,
  }: StepConfig): Promise<boolean> {
    try {
      v$.value.$touch();
      if (v$.value.$error) return false;
      // Raised for the save so the flow's buttons are disabled: a second click on Next while the
      // first save of a new company is in flight would post it again and create a second company.
      isLoading.value = true;
      return apiEndpoint ? await saveStep(apiEndpoint) : true;
    } catch (error) {
      globalErrorHandler(error);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  /** Takes the answer of a file upload, which posts its own request, into both stores and the URL. */
  async function takeUpload({
    accountData,
    company_id,
    generator_id,
  }: GeneratorUploadResult): Promise<void> {
    signupStore.takeAnswer(accountData);
    await updateQuery({
      generator_id: String(generator_id),
      company_id: String(company_id),
    });
  }

  /** False when the page moved to another country or company while the save was in flight: the step stays put. */
  async function saveStep(url: string): Promise<boolean> {
    const savedFor = selection();
    const { company_id, generator_id } = await signupStore.saveDraft(url);
    // Lowered before the URL push as well as in `finally`, so the overlay is down while it lands.
    isLoading.value = false;
    // Back/forward to another country or company while the save was in flight: its ids, and the next
    // step, belong to what the page showed before, so neither goes onto the new URL.
    if (!isEqual(selection(), savedFor)) return false;
    await updateQuery({
      company_id: String(company_id),
      ...(generator_id && { generator_id: String(generator_id) }),
    });
    return true;
  }

  function selection() {
    return { country: signupStore.country, companyId: route.query.company_id };
  }

  return { postStepCallback, takeUpload };
}
